import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { logger } from '@musedrops/shared';
import { supabase } from '../../../services/supabase';
import { ProtectedDeleteDialog } from '../../../shared/components/ProtectedDeleteDialog';
import { isProduction } from '../../../config/env';
import { createStoryIdeasJob } from '../../../hooks/useStoryIdeas';

interface ShowBulkActionsProps {
  selectedIds: string[];
  /** Map of show ID → slug for selected shows */
  showSlugs: Record<string, string>;
  onComplete: () => void;
  onNotification: (message: string, type: 'success' | 'error') => void;
}

export const ShowBulkActions: React.FC<ShowBulkActionsProps> = ({
  selectedIds,
  showSlugs,
  onComplete,
  onNotification,
}) => {
  const [showProtectedDialog, setShowProtectedDialog] = useState(false);
  const [showStandardDialog, setShowStandardDialog] = useState(false);
  const [hasEnabledShows, setHasEnabledShows] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Generate stories dialog
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [storiesPerShow, setStoriesPerShow] = useState(1);
  const [generating, setGenerating] = useState(false);

  const count = selectedIds.length;
  const totalStories = count * storiesPerShow;

  const checkAndShowDialog = async () => {
    if (count === 0) return;

    // In development, always use standard confirmation
    if (!isProduction()) {
      setShowStandardDialog(true);
      return;
    }

    // In production, check if any selected shows are enabled
    const { data, error } = await supabase
      .from('shows')
      .select('id, enabled')
      .in('id', selectedIds);

    if (error) {
      logger.error('Failed to check show status', { error });
      setShowStandardDialog(true);
      return;
    }

    const hasEnabled = data?.some(show => show.enabled === true) ?? false;
    setHasEnabledShows(hasEnabled);

    if (hasEnabled) {
      setShowProtectedDialog(true);
    } else {
      setShowStandardDialog(true);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { error } = await supabase.from('shows').delete().in('id', selectedIds);

      if (error) {
        logger.error('Failed to delete shows', { error, count });
        return;
      }

      logger.info('Deleted shows', { count });
      onComplete();
      setShowProtectedDialog(false);
      setShowStandardDialog(false);
    } catch (error) {
      logger.error('Error deleting shows', { error });
    } finally {
      setDeleting(false);
    }
  };

  const handleGenerateStories = async () => {
    setGenerating(true);
    try {
      const slugs = selectedIds.map(id => showSlugs[id]).filter((s): s is string => Boolean(s));

      const results = await Promise.all(
        slugs.map(slug =>
          createStoryIdeasJob({
            showSlug: slug,
            storyIdeaCount: storiesPerShow,
            shouldSave: true,
          })
        )
      );

      const succeeded = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).length;

      setShowGenerateDialog(false);

      if (failed === 0) {
        onNotification(
          `Queued ${totalStories} ${totalStories === 1 ? 'story' : 'stories'} for ${succeeded} ${succeeded === 1 ? 'show' : 'shows'}`,
          'success'
        );
      } else {
        onNotification(
          `Queued for ${succeeded} shows, failed for ${failed}`,
          failed === slugs.length ? 'error' : 'success'
        );
      }

      onComplete();
    } catch (error) {
      logger.error('Error generating stories', { error });
      onNotification('Failed to queue story generation', 'error');
      setShowGenerateDialog(false);
    } finally {
      setGenerating(false);
    }
  };

  const handleCloseDialogs = () => {
    if (deleting) return;
    setShowProtectedDialog(false);
    setShowStandardDialog(false);
  };

  return (
    <>
      {/* Left-side actions (rendered next to selection count by parent) */}
      <Button
        onClick={() => setShowGenerateDialog(true)}
        disabled={count === 0}
        color="primary"
        variant="outlined"
        size="small"
      >
        Generate Stories
      </Button>

      {/* Spacer pushes delete to the right */}
      <Box sx={{ flex: 1 }} />

      {/* Right-side actions */}
      <Button
        onClick={checkAndShowDialog}
        disabled={count === 0 || deleting}
        color="error"
        variant="outlined"
        size="small"
        startIcon={deleting ? <CircularProgress size={16} /> : null}
      >
        {deleting ? 'Deleting...' : `Delete (${count})`}
      </Button>

      {/* Generate stories dialog */}
      <Dialog
        open={showGenerateDialog}
        onClose={() => !generating && setShowGenerateDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Generate Stories</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Generate stories for {count} selected {count === 1 ? 'show' : 'shows'}.
          </Typography>
          <TextField
            label="Stories per show"
            type="number"
            value={storiesPerShow}
            onChange={e => {
              const val = Math.max(1, Math.min(20, parseInt(e.target.value, 10) || 1));
              setStoriesPerShow(val);
            }}
            inputProps={{ min: 1, max: 20 }}
            size="small"
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowGenerateDialog(false)}
            color="inherit"
            disabled={generating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerateStories}
            variant="contained"
            disabled={generating}
            startIcon={generating ? <CircularProgress size={16} /> : null}
          >
            {generating
              ? 'Queuing...'
              : `Generate ${totalStories} ${totalStories === 1 ? 'story' : 'stories'}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Protected dialog for enabled shows */}
      <ProtectedDeleteDialog
        open={showProtectedDialog}
        onClose={handleCloseDialogs}
        onConfirm={handleDelete}
        title="Delete Enabled Shows"
        message={
          hasEnabledShows
            ? `You are about to delete ${count} ${count === 1 ? 'show' : 'shows'}. ${
                count === 1
                  ? 'This show is enabled and may be actively used.'
                  : 'Some of these shows are enabled and may be actively used.'
              }`
            : `Are you sure you want to delete ${count} ${count === 1 ? 'show' : 'shows'}?`
        }
      />

      {/* Standard confirmation dialog */}
      <Dialog
        open={showStandardDialog}
        onClose={handleCloseDialogs}
        maxWidth="sm"
      >
        <DialogTitle>Delete Shows</DialogTitle>
        <DialogContent>
          Are you sure you want to delete {count}{' '}
          {count === 1 ? 'show' : 'shows'}?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs} color="inherit" disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : null}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
